<div *ngIf="loading || loadFailed" class="loading-container"><app-loader variant="almostready" [failed]="loadFailed" (retry)="loadData()"></app-loader></div>
<div *ngIf="!loading && !loadFailed && isMaccabiRoute" class="page report-back-page screen-only">
  <button class="pl-back" type="button" (click)="goBack()">{{ 'doctorReport.reportView.v4.backToList' | translate }}</button>
</div>
<div *ngIf="!loading && !loadFailed && !data" class="page page-container">
  <div class="card">
    <div class="card-title">{{ 'doctorReport.reportView.v4.noReportTitle' | translate }}</div>
    <div class="flag-text">{{ rv('noReportBody') }}</div>
  </div>
</div>

<!-- The record layout follows the clinical evaluation design: an identity
     masthead, then three tabs. On paper every tab prints, so the printed report
     is still the whole record in one run. -->
<div *ngIf="!loading && data" class="page page-container report-v5">

<!-- THE HEAD: identity and tabs in one white block, as the record design has it. -->
<div class="rv-head">

<!-- BRAND LINE -->
<div class="rv-brandline">
  <div class="header-brand">
    <!-- Maccabi co-brand only inside the Maccabi doctor portal; a consumer's own
         report (no provider assigned) shows MARKABLE alone. -->
    <ng-container *ngIf="isMaccabiRoute">
      <span class="brand-maccabi">{{ 'doctorReport.reportView.v4.brandMaccabi' | translate }}</span>
      <span class="brand-sep">|</span>
    </ng-container>
    <img class="brand-markable-logo" [src]="logoSrc" alt="MARKABLE">
  </div>
  <div class="rv-doctitle">{{ 'doctorReport.reportView.v4.title' | translate }}</div>
</div>

<!-- MASTHEAD -->
<div class="rv-masthead">
  <div class="rv-identity">
    <div class="rv-avatar" aria-hidden="true">{{ patientInitials }}</div>
    <div class="rv-identity-text">
      <h1>{{ data.patientName || ('doctorReport.reportView.v4.patientFallback' | translate) }}</h1>
      <p>
        <span>{{ 'doctorReport.reportView.age' | translate }} {{ data.patientAge || '-' }}</span>
        <span class="rv-sep">·</span>
        <span>{{ mappingCountText }}</span>
        <span class="rv-sep">·</span>
        <span>{{ rv5('latestMapping', { date: reportDate(data.checkDate) || '-' }) }}</span>
        <span class="rv-sep" *ngIf="hrtWeeks !== null">·</span>
        <span *ngIf="hrtWeeks !== null">{{ 'doctorReport.reportView.v4.weeksSinceHrt' | translate:{ week: hrtWeeks } }}</span>
        <!-- Fix 45: first-scan tag so a clinician doesn't read the missing trends
             as a broken / incomplete report. -->
        <span class="rv-first-tag" *ngIf="isSingleCheck">{{ 'doctorReport.reportView.v4.firstScanPill' | translate }}</span>
      </p>
    </div>
  </div>
  <!-- The record's own action corner: the one thing a clinician does with this
       page is the filled button, and printing is the quiet line under it. -->
  <div class="rv-actions screen-only">
    <button class="btn btn-primary" type="button" (click)="openEhrDialog()">{{ rv5('reviewCopyToRecord') }} <svg class="rv-arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg></button>
    <div class="rv-secondary-actions">
      <button type="button" [disabled]="printBusy" (click)="printReport()">{{ 'doctorReport.reportView.v4.print' | translate }}</button>
      <p class="rv-print-hint" *ngIf="printHint">{{ printHint }}</p>
    </div>
  </div>
</div>

<!-- TABS -->
<nav class="rv-tabs screen-only" role="tablist">
  <button *ngFor="let tab of recordTabs"
          type="button"
          role="tab"
          [id]="'rv-tab-' + tab.id"
          [attr.aria-selected]="activeTab === tab.id"
          [attr.tabindex]="activeTab === tab.id ? 0 : -1"
          (click)="setTab(tab.id)">
    <span class="rv-tab-index">{{ tab.index }}</span>{{ rv5(tab.labelKey) }}
  </button>
</nav>

</div><!-- /rv-head -->

<!-- ======================= 01 CLINICAL SUMMARY ======================= -->
<section class="rv-panel" [class.is-open]="activeTab === 'summary'" role="tabpanel" aria-labelledby="rv-tab-summary">

  <div class="rv-panel-intro">
    <h2>{{ rv5('summaryIntro') }}</h2>
    <span>{{ rv5('summaryIntroMeta', { date: reportDate(data.checkDate) || '-' }) }}</span>
  </div>

  <!-- HER WORDS + HER PRIORITIES -->
  <section class="rv-card rv-visit" *ngIf="visitQuote || priorityItems.length">
    <ng-container *ngIf="visitQuote">
      <div class="rv-eyebrow">{{ rv5('visitEyebrow') }}</div>
      <blockquote>{{ visitQuote }}</blockquote>
    </ng-container>
    <div class="rv-priorities" *ngIf="priorityItems.length">
      <div class="rv-eyebrow">{{ rv5('prioritiesEyebrow') }}</div>
      <ol>
        <li *ngFor="let item of priorityItems; let i = index">
          <span class="rv-priority-number">{{ i + 1 }}</span>
          <span class="rv-priority-label">{{ item.label }}</span>
          <span class="rv-priority-value" *ngIf="item.value !== null">{{ item.value }}<small>/10</small></span>
        </li>
      </ol>
    </div>
  </section>

  <!-- SYMPTOM SUMMARY -->
  <section class="rv-card rv-digest" *ngIf="digestCounts.positive">
    <div class="rv-panel-heading">
      <div>
        <div class="rv-eyebrow">{{ rv5('digestEyebrow') }}</div>
        <h2>{{ digestTitle }}</h2>
      </div>
      <!-- Offered only when she reported starting something and there is a mapping
           from before that date to compare against. -->
      <div class="rv-period-switch screen-only" *ngIf="canCompareTreatment && !isSingleCheck" [attr.aria-label]="rv5('digestEyebrow')">
        <button type="button" [attr.aria-pressed]="comparisonMode === 'treatment'" (click)="setComparisonMode('treatment')">{{ rv5('switchTreatment') }}</button>
        <button type="button" [attr.aria-pressed]="comparisonMode === 'previous'" (click)="setComparisonMode('previous')">{{ rv5('switchPrevious') }}</button>
      </div>
    </div>
    <div class="rv-digest-counts">
      <span class="rv-count-lead"><b>{{ digestCounts.positive }}</b> {{ rv5('countAboveZero') }}</span>
      <span>{{ rv5('countHigh', { n: digestCounts.high }) }}</span>
      <span>{{ rv5('countModerate', { n: digestCounts.moderate }) }}</span>
      <span>{{ rv5('countLow', { n: digestCounts.low }) }}</span>
    </div>
    <p class="rv-range" *ngIf="!isSingleCheck && comparisonRangeText">{{ comparisonRangeText }}</p>

    <!-- Fix 52: "went down / went up" describe the patient's state, so the
         self-rating + no-causal-link line stays attached to them. -->
    <div class="rv-directions" *ngIf="!isSingleCheck">
      <div class="rv-direction rv-lower">
        <div class="rv-direction-head">
          <span class="rv-direction-mark">&#8722;</span>
          <h3>{{ rv5('dirLower') }}</h3>
          <span class="rv-direction-total">{{ rv5('dirTotal', { n: lowerChanges.length }) }}</span>
        </div>
        <ng-container *ngIf="lowerChanges.length; else noLower">
          <div class="rv-change-head">
            <span>{{ rv5('colLargest') }}</span><span>{{ rv5('colBaseline') }}</span><span>{{ rv5('colLatest') }}</span><span>{{ rv5('colChange') }}</span>
          </div>
          <div class="rv-change-row" *ngFor="let row of lowerChanges.slice(0, 3)">
            <span class="rv-change-label">{{ row.label }}</span>
            <span class="rv-change-prev">{{ row.prev }}</span>
            <strong class="rv-change-now">{{ row.now }}<small>/10</small></strong>
            <span class="rv-change-delta">{{ deltaText(row.delta) }}</span>
          </div>
        </ng-container>
        <ng-template #noLower><p class="rv-none">{{ rv5('noneLower') }}</p></ng-template>
      </div>

      <div class="rv-direction rv-higher">
        <div class="rv-direction-head">
          <span class="rv-direction-mark">+</span>
          <h3>{{ rv5('dirHigher') }}</h3>
          <span class="rv-direction-total">{{ rv5('dirTotal', { n: higherChanges.length }) }}</span>
        </div>
        <ng-container *ngIf="higherChanges.length; else noHigher">
          <div class="rv-change-head">
            <span>{{ rv5('colLargest') }}</span><span>{{ rv5('colBaseline') }}</span><span>{{ rv5('colLatest') }}</span><span>{{ rv5('colChange') }}</span>
          </div>
          <div class="rv-change-row" *ngFor="let row of higherChanges.slice(0, 3)">
            <span class="rv-change-label">{{ row.label }}</span>
            <span class="rv-change-prev">{{ row.prev }}</span>
            <strong class="rv-change-now">{{ row.now }}<small>/10</small></strong>
            <span class="rv-change-delta">{{ deltaText(row.delta) }}</span>
          </div>
        </ng-container>
        <ng-template #noHigher><p class="rv-none">{{ rv5('noneHigher') }}</p></ng-template>
      </div>
    </div>
    <p class="rv-footnote" *ngIf="!isSingleCheck">{{ rv('changeDisclaimer') }}</p>
    <!-- Fix 45: one explicit baseline note on a first mapping. -->
    <p class="rv-footnote" *ngIf="isSingleCheck">{{ 'doctorReport.reportView.v4.firstScanBaseNote' | translate }}</p>

    <details class="rv-quiet">
      <summary><span>{{ rv5('allRatings') }}</span><span>{{ rv5('ratingsCount', { n: ratingTableRows.length }) }}</span></summary>
      <table class="rv-table">
        <thead>
          <tr>
            <th></th>
            <th>{{ rv5('colLatest') }}</th>
            <th *ngIf="!isSingleCheck">{{ rv5('colBaseline') }}</th>
            <th *ngIf="!isSingleCheck">{{ rv5('colChange') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of ratingTableRows">
            <td>{{ row.label }}</td>
            <td>{{ row.now }}/10</td>
            <td *ngIf="!isSingleCheck">{{ row.prev === null ? '-' : row.prev }}</td>
            <td *ngIf="!isSingleCheck">{{ row.delta === null ? '-' : deltaText(row.delta) }}</td>
          </tr>
        </tbody>
      </table>
    </details>
  </section>

  <!-- COPY TO CHART. On screen this lives in the review dialog the masthead opens;
       on paper it prints with the rest of the record, as it always did. -->
  <div class="navy-card print-only">
    <div class="card-title">{{ rv('ehrTitle') }}</div>
    <div class="ehr-text">{{ ehrText }}</div>
  </div>

  <!-- ACCORDIONS: what she reported, folded away until asked for -->
  <div class="rv-accordions">
    <!-- Fix 46: no adherence RING and no "82%" - that number was the mean of four
         hand-picked category constants, not a rate of anything measured. The
         per-supplement reported category is what a clinician can use. -->
    <details class="rv-accordion" *ngIf="medications.length">
      <summary>
        <span class="rv-acc-index">01</span>
        <span class="rv-acc-title"><b>{{ rv5('accMeds') }}</b><small>{{ rv5('accMedsMeta', { n: medications.length }) }}</small></span>
        <i aria-hidden="true">+</i>
      </summary>
      <div class="rv-acc-body">
        <div class="reg-caption" *ngIf="!isSingleCheck">{{ 'doctorReport.reportView.v4.reportedIntakeLabel' | translate }}</div>
        <div class="regimen-item" *ngFor="let med of medications">
          <div class="reg-dot" [ngClass]="isHrt(med) ? 'reg-dot-hrt' : 'reg-dot-other'"></div>
          <div>
            <div class="reg-name">{{ med.name || ('doctorReport.reportView.v4.unnamedMedication' | translate) }}</div>
            <div class="reg-detail">
              {{ med.dose || '' }} {{ formatFrequency(med.frequency, med.frequencyCustom) }}
              <span *ngIf="med.startDate"> | {{ 'doctorReport.reportView.v4.medFrom' | translate:{ date: reportDate(med.startDate) } }}</span>
              <span *ngIf="med.reason"> | {{ med.reason }}</span>
            </div>
          </div>
          <!-- Fix 45: no consistency chip on a first scan - it can't be computed
               without history. One card-level line explains it instead. -->
          <span *ngIf="!isSingleCheck" class="badge" [ngClass]="isHrt(med) ? 'badge-green' : 'badge-gray'" style="margin-inline-start:auto">{{ formatConsistency(med.consistency) }}</span>
        </div>
        <div class="reg-first-note" *ngIf="isSingleCheck">{{ 'doctorReport.reportView.v4.supplementFirstNote' | translate }}</div>
        <!-- Reported sensations belong next to the regimen they are reported against. -->
        <div class="side-effect-strip" *ngIf="data.sideEffects.length">
          <span class="side-effect-label">{{ 'doctorReport.reportView.v4.sideEffectsStrip' | translate }}</span>
          <span class="side-effect-pill" *ngFor="let item of data.sideEffects">{{ item }}</span>
        </div>
      </div>
    </details>

    <!-- Only the background rows we actually ask for. The design's remaining rows
         (family history, BMI, blood pressure, screening) are absent because we do
         not collect them, and an empty row would read as a denial. -->
    <details class="rv-accordion" *ngIf="historyRows.length">
      <summary>
        <span class="rv-acc-index">{{ medications.length ? '02' : '01' }}</span>
        <span class="rv-acc-title"><b>{{ rv5('accHistory') }}</b><small>{{ historyRows.length === 1 ? rv5('accHistoryMetaOne') : rv5('accHistoryMeta', { n: historyRows.length }) }}</small></span>
        <i aria-hidden="true">+</i>
      </summary>
      <div class="rv-acc-body">
        <table class="rv-table">
          <tbody>
            <tr *ngFor="let row of historyRows">
              <th>{{ row.label }}</th>
              <td>{{ row.value }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
  </div>

  <!-- SAFETY (self-report) — its own section down the record (Danna 16.09), and
       still deliberately NOT scored/banded/colored (safety spec 2026-08-17): the
       patient flagged these; they are not part of the symptom-load score. -->
  <section class="rv-card rv-safety" *ngIf="data.safetyFlags?.length">
    <div class="rv-eyebrow">{{ 'doctorReport.reportView.v4.safetyTitle' | translate }}</div>
    <ul class="rv-safety-items">
      <li *ngFor="let f of data.safetyFlags">{{ ('symptomsForm.sweep.safety.items.' + f) | translate }}</li>
    </ul>
    <div class="rv-safety-note">{{ 'doctorReport.reportView.v4.safetyNote' | translate }}</div>
  </section>

  <!-- BEFORE YOU FINISH -->
  <section class="rv-card rv-conversation">
    <div>
      <div class="rv-eyebrow">{{ rv5('conversationEyebrow') }}</div>
      <h2>{{ rv5('conversationTitle') }}</h2>
      <p>{{ rv5('conversationBody') }}</p>
    </div>
    <div class="rv-prompts">
      <p><b>01</b> {{ rv5('conversationQ1') }}</p>
      <p><b>02</b> {{ rv5('conversationQ2') }}</p>
      <!-- The record ends where the note begins: the same summary dialog, opened
           from the card that asks her to confirm it. -->
      <button class="rv-text-action screen-only" type="button" (click)="openEhrDialog()">{{ rv5('conversationReview') }} <svg class="rv-arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg></button>
    </div>
  </section>
</section>

<!-- ======================= 02 TIMELINE ======================= -->
<section class="rv-panel" [class.is-open]="activeTab === 'timeline'" role="tabpanel" aria-labelledby="rv-tab-timeline">

  <div class="rv-panel-intro">
    <h2>{{ rv5('timelineHeading') }}</h2>
    <span>{{ timelineRangeText }}</span>
  </div>

  <p class="rv-notice" *ngIf="!hasTrends">{{ rv5('firstMappingNotice') }}</p>

  <!-- The chart and the controls that compose it. On a first mapping there is
       nothing to draw, so the workspace collapses to the clusters below. -->
  <section class="rv-card rv-timeline" *ngIf="hasTrends">
    <div class="rv-workspace">
      <aside class="rv-controls screen-only">
        <h3>{{ rv5('layersTitle') }}</h3>
        <label class="rv-layer-pill">
          <input type="checkbox" [checked]="showSymptomLayer" (change)="showSymptomLayer = !showSymptomLayer">
          <span>{{ rv5('layerSymptoms') }}</span>
        </label>
        <label class="rv-layer-pill" *ngIf="medications.length">
          <input type="checkbox" [checked]="showTreatmentLayer" (change)="showTreatmentLayer = !showTreatmentLayer">
          <span>{{ rv5('layerTreatments') }}</span>
        </label>
        <!-- The effects she wrote on her mappings. The pill is always offered so
             the layer is discoverable, and says plainly when she reported none. -->
        <label class="rv-layer-pill" [class.is-empty]="!hasSideEffectEvents"
               [attr.title]="hasSideEffectEvents ? null : rv5('layerSideEffectsEmpty')">
          <input type="checkbox" [checked]="showSideEffectLayer" [disabled]="!hasSideEffectEvents"
                 (change)="toggleSideEffectLayer()">
          <span>{{ rv5('layerSideEffects') }}</span>
        </label>

        <details class="rv-layer-picker">
          <summary><span>{{ rv5('chooseSymptoms') }}</span><i aria-hidden="true">+</i></summary>
          <div class="rv-bulk">
            <button type="button" (click)="bulkSeries('all')">{{ rv5('bulkAll') }}</button>
            <button type="button" (click)="bulkSeries('high')">{{ rv5('bulkHigh') }}</button>
            <button type="button" (click)="bulkSeries('none')">{{ rv5('bulkNone') }}</button>
          </div>
          <div class="rv-series-list">
            <label *ngFor="let series of availableSeries">
              <input type="checkbox" [checked]="series.selected" (change)="toggleSeries(series.key)">
              <i [style.background]="series.color"></i>
              <span>{{ series.label }}</span>
              <b>{{ series.latest }}</b>
            </label>
          </div>
        </details>
        <small>{{ rv5('scaleNote') }}</small>
      </aside>

      <div class="rv-chart">
        <div class="timeline-coverage">{{ timelineCoverageText }}</div>
        <svg id="mainChart" viewBox="0 0 700 320" width="100%" preserveAspectRatio="xMidYMid meet" style="display:block">
          <defs>
            <clipPath id="chartClip"><rect x="40" y="10" width="640" height="260"/></clipPath>
          </defs>

          <!-- Treatment periods she reported, behind the lines. -->
          <g clip-path="url(#chartClip)">
            <rect *ngFor="let band of treatmentBands" [attr.x]="band.x" y="10" [attr.width]="band.width" height="260" [attr.fill]="band.color"></rect>
          </g>

          <g class="ch-grid" stroke-width="0.5">
            <line x1="40" y1="10" x2="40" y2="270"/>
            <line x1="40" y1="270" x2="680" y2="270"/>
            <line *ngFor="let row of gridRows" x1="40" [attr.y1]="yForScore(row)" x2="680" [attr.y2]="yForScore(row)" stroke-dasharray="2,4"/>
          </g>

          <g class="ch-axis" text-anchor="end">
            <text *ngFor="let row of gridRows" x="36" [attr.y]="yForScore(row) + 4">{{ row }}</text>
          </g>

          <!-- Fix 29a: date axis, one tick per rated scan (not the 1..61 index). -->
          <g class="ch-axis" text-anchor="middle">
            <line *ngFor="let tick of dateAxisTicks" [attr.x1]="tick.x" y1="270" [attr.x2]="tick.x" y2="275" class="ch-tick" stroke-width=".6"/>
            <text *ngFor="let tick of dateAxisTicks" [attr.x]="tick.x" [attr.y]="tick.y" [attr.text-anchor]="tick.anchor">{{ tick.label }}</text>
          </g>

          <g *ngIf="showTreatmentLayer">
            <g *ngFor="let event of hrtEvents">
              <rect [attr.x]="xForEventWeek(event.week) - 1.5" y="8" width="3" height="264" [attr.fill]="event.color" rx="1.5" opacity=".7"/>
              <rect [attr.x]="xForEventWeek(event.week) - 55" y="4" width="110" height="16" [attr.fill]="event.color" rx="3" opacity=".9"/>
              <text [attr.x]="xForEventWeek(event.week)" y="15" font-size="8.5" fill="#fff" font-weight="700" text-anchor="middle">{{ event.label }} - W{{ event.week }}</text>
            </g>
          </g>

          <!-- Reported side effects, on the date she wrote them. -->
          <g class="rv-side-effect-marks" *ngIf="sideEffectMarks.length">
            <g *ngFor="let mark of sideEffectMarks">
              <path [attr.d]="'M ' + mark.x + ' ' + (mark.y - 7) + ' l 7 7 -7 7 -7 -7 Z'"
                    fill="#B85B46" stroke="#fff" stroke-width="1.6">
                <title>{{ mark.title }}</title>
              </path>
              <text [attr.x]="mark.labelX" [attr.y]="mark.labelY" [attr.text-anchor]="mark.anchor"
                    class="rv-effect-label">{{ mark.label }}</text>
            </g>
          </g>

          <g clip-path="url(#chartClip)">
            <g *ngFor="let series of timelineSeries">
              <polyline class="symptom-line"
                [attr.points]="series.points"
                fill="none"
                [attr.stroke]="series.color"
                [attr.stroke-width]="series.dashed ? 1.6 : 2.5"
                [attr.stroke-dasharray]="series.dashed ? '4,3' : null"
                stroke-linecap="round"
                stroke-linejoin="round"/>
              <circle *ngFor="let point of series.values" [attr.cx]="point.x" [attr.cy]="point.y" r="3.5" [attr.fill]="series.color">
                <title>#{{ point.scanNumber }} | {{ reportDate(point.date) }} | W{{ point.week }} | {{ point.value }}/10</title>
              </circle>
            </g>
          </g>
        </svg>

        <p class="rv-none" *ngIf="!timelineSeries.length">{{ rv5('noSeriesChosen') }}</p>

        <div class="legend" id="chartLegend">
          <div class="legend-item" *ngFor="let series of timelineSeries">
            <div class="legend-dot" [ngClass]="{ dashed: series.dashed }" [style.background]="series.dashed ? 'transparent' : series.color" [style.border-color]="series.color"></div>
            <span>{{ series.label }}</span>
            <span class="legend-change">{{ series.changeText }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- What she started and when, folded away under the chart it explains. -->
    <details class="rv-quiet" *ngIf="medicationChronology.length">
      <summary><span>{{ rv5('medChronology') }}</span><span>{{ rv5('accMedsMeta', { n: medicationChronology.length }) }}</span></summary>
      <p class="rv-footnote">{{ rv5('medChronologyNote') }}</p>
      <table class="rv-table">
        <tbody>
          <tr *ngFor="let row of medicationChronology">
            <th>{{ row.date }}</th>
            <td><b>{{ row.label }}</b><span *ngIf="row.detail"> · {{ row.detail }}</span></td>
          </tr>
        </tbody>
      </table>
    </details>
  </section>

  <!-- ADDITIONAL TRENDS. Her symptom groups are not repeated here - they are read
       in the Explorer, group by group. What belongs under the chart is what the
       chart cannot carry: the overall total, and each domain on its own scale. -->
  <section class="rv-card rv-trends" *ngIf="hasAdditionalTrends">
    <div class="rv-panel-heading">
      <h2>{{ rv5('trendsTitle') }}</h2>
    </div>
    <p class="rv-footnote">{{ rv5('trendsMeta') }}</p>

    <!-- The overall symptom total: one number, its history, and the sum behind it. -->
    <div class="rv-total-strip" *ngIf="symptomTotalRows.length">
      <div class="rv-total-head">
        <h3>{{ rv5('trendTotalTitle') }}<span class="rv-info" tabindex="0"
          role="note" [attr.aria-label]="rv5('trendTotalInfo')"
          ><span aria-hidden="true">i</span><span class="rv-info-bubble">{{ rv5('trendTotalInfo') }}</span></span></h3>
        <p>{{ rv5('trendTotalNote') }}</p>
      </div>
      <strong class="rv-total-value">{{ symptomTotalLatest }}</strong>
      <div class="rv-total-chart">
        <ng-container *ngTemplateOutlet="miniChart; context: { $implicit: symptomTotalChart, bare: true }"></ng-container>
      </div>
      <span class="rv-total-move">{{ symptomTotalMovement }}</span>
      <details class="rv-quiet rv-total-details">
        <summary><span class="rv-trend-line"><span>{{ rv5('trendTotalByMapping') }}</span></span></summary>
        <table class="rv-table">
          <tbody>
            <tr *ngFor="let row of symptomTotalRows">
              <th>{{ row.dateText }}</th>
              <td><b>{{ row.total === null ? rv5('trendNotMeasured') : row.total }}</b> &middot; {{ rv5('trendRatingsAvailable', { n: row.count }) }}</td>
            </tr>
          </tbody>
        </table>
      </details>
    </div>

    <div class="rv-trend-grid">
      <article class="rv-trend-domain" *ngIf="cognitiveTrendRows.length">
        <h3>{{ rv5('trendCognitiveTitle') }}</h3>
        <p class="rv-trend-meta">{{ cognitiveTrendMeta }}</p>
        <details *ngFor="let row of cognitiveTrendRows">
          <summary><span class="rv-trend-line"><span>{{ row.label }}</span><ng-container
            *ngTemplateOutlet="trendRange; context: { $implicit: row }"></ng-container></span></summary>
          <ng-container *ngTemplateOutlet="miniChart; context: { $implicit: row.chart }"></ng-container>
          <p class="rv-footnote">{{ rv5('trendAllValues', { values: row.valuesText }) }}</p>
        </details>
      </article>

      <article class="rv-trend-domain" *ngFor="let row of senseTrendRows">
        <h3>{{ row.label }}</h3>
        <p class="rv-trend-meta">{{ row.meta }}</p>
        <strong class="rv-trend-range">
          <ng-container *ngTemplateOutlet="trendRange; context: { $implicit: row }"></ng-container>
        </strong>
        <ng-container *ngTemplateOutlet="miniChart; context: { $implicit: row.chart }"></ng-container>
        <p class="rv-footnote">{{ rv5('trendSenseNote') }}</p>
      </article>

      <article class="rv-trend-domain rv-trend-bands" *ngIf="facialTrendRows.length">
        <h3>{{ rv5('trendFacialTitle') }}</h3>
        <p class="rv-trend-meta">{{ rv5('trendFacialNote') }}</p>
        <!-- Which way the numbers read. Said once, quietly, and per marker:
             moisture is the one where the higher value is the easier one. -->
        <p class="rv-trend-meta rv-trend-direction">{{ rv5('trendFacialDirection') }}</p>
        <details *ngFor="let row of facialTrendRows">
          <summary><span class="rv-trend-line"><span>{{ row.label }}</span><ng-container
            *ngTemplateOutlet="trendRange; context: { $implicit: row }"></ng-container></span></summary>
          <div class="rv-band-chips">
            <div class="rv-band-chip" *ngFor="let point of row.points">
              <span class="rv-band-date">{{ point.dateText }}</span>
              <b class="rv-band-value">{{ point.valueText }}</b>
            </div>
          </div>
        </details>
      </article>
    </div>
  </section>

  <!-- The two ends of a series. Each number keeps its own left-to-right reading
       (a negative dB value must not flip in Hebrew); only the word between them
       follows the page. -->
  <ng-template #trendRange let-row>
    <b class="rv-ends"><span dir="ltr">{{ row.firstText }}</span><ng-container *ngIf="row.lastText"><span
      class="rv-ends-to">{{ rv5('trendRangeTo') }}</span><span dir="ltr">{{ row.lastText }}</span></ng-container></b>
  </ng-template>

  <!-- One drawing, used by every trend above: a line on its own scale, the gaps
       left open where a mapping did not measure it, and the two end dates. -->
  <ng-template #miniChart let-chart let-bare="bare">
    <p class="rv-none" *ngIf="chart.empty">{{ rv5('trendNotMeasured') }}</p>
    <svg *ngIf="!chart.empty" class="rv-mini-chart" [class.rv-mini-bare]="bare"
      [attr.viewBox]="bare ? '34 4 352 68' : '0 0 420 96'" width="100%" preserveAspectRatio="xMidYMid meet">
      <line *ngIf="!bare" x1="40" x2="380" y1="66" y2="66"/>
      <polyline *ngFor="let segment of chart.segments" [attr.points]="segment" fill="none" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <circle *ngFor="let dot of chart.dots" [attr.cx]="dot.x" [attr.cy]="dot.y" r="4"><title>{{ dot.title }}</title></circle>
      <text *ngIf="!bare" x="40" y="88">{{ chart.firstLabel }}</text>
      <text *ngIf="!bare" x="380" y="88" text-anchor="end">{{ chart.lastLabel }}</text>
    </svg>
  </ng-template>
</section>

<!-- ======================= 03 EXPLORER ======================= -->
<section class="rv-panel" [class.is-open]="activeTab === 'detail'" role="tabpanel" aria-labelledby="rv-tab-detail">

  <div class="rv-panel-intro">
    <div>
      <div class="rv-eyebrow">{{ rv5('explorerEyebrow') }}</div>
      <h2>{{ rv5('explorerTitle') }}</h2>
      <p class="rv-footnote">{{ rv5('explorerIntro') }}</p>
    </div>
  </div>

  <!-- PAIRINGS, first: a symptom she reported beside the measurement of ours that
       looks at the same ground, each pair read out in one plain sentence. Two
       scales side by side, and nothing inferred from putting them there. -->
  <section class="rv-card" *ngIf="symptomMeasurePairs.length">
    <div class="rv-panel-heading">
      <h2>{{ rv5('pairsTitle') }}</h2>
    </div>
    <div class="rv-fusion-grid">
      <article class="rv-fusion" *ngFor="let pair of symptomMeasurePairs">
        <div class="rv-fusion-body">
          <div class="rv-fusion-side">
            <span class="rv-eyebrow">{{ rv5('pairsReported') }}</span>
            <h4>{{ pair.symptomLabel }}</h4>
            <strong class="rv-ends"><span dir="ltr" *ngIf="pair.symptomPrev !== null">{{ pair.symptomPrev }}</span><span
              class="rv-ends-to" *ngIf="pair.symptomPrev !== null">{{ rv5('trendRangeTo') }}</span><span
              dir="ltr">{{ pair.symptomNow }}<small>/10</small></span></strong>
            <p>{{ rv5('pairsScale') }}</p>
          </div>
          <span class="rv-fusion-link" aria-hidden="true">+</span>
          <div class="rv-fusion-side">
            <span class="rv-eyebrow">{{ rv5(pair.source === 'facial' ? 'pairsSelfie' : pair.source === 'sense' ? 'pairsSelfCheck' : 'pairsTask') }}</span>
            <h4>{{ pair.measureLabel }}</h4>
            <strong class="rv-ends"><span dir="ltr" *ngIf="pair.measureFirst">{{ pair.measureFirst }}</span><span
              class="rv-ends-to" *ngIf="pair.measureFirst">{{ rv5('trendRangeTo') }}</span><span
              dir="ltr">{{ pair.measureLast }}<small *ngIf="pair.measureUnit">{{ pair.measureUnit }}</small></span></strong>
            <p>{{ pair.rangeText }}</p>
          </div>
        </div>
        <p class="rv-fusion-read">{{ pair.reading }}</p>
      </article>
    </div>
    <p class="rv-footnote">{{ rv5('pairsNote') }}</p>
  </section>

  <!-- FOUR PERSPECTIVES: the three things she performed and what the selfie read,
       each one an entry card that opens onto its own detail. -->
  <section class="rv-card">
    <div class="rv-panel-heading">
      <h2>{{ rv5('fourPerspectives') }}</h2>
      <span class="rv-panel-meta">{{ rv5('perspectivesMeta') }}</span>
    </div>

    <div class="rv-domain-grid">
      <!-- 01 the cognitive battery -->
      <details class="rv-domain" *ngIf="cognitiveRows.length">
        <summary>
          <span class="rv-domain-no">{{ rv5('perspectiveCognitiveEyebrow') }}</span>
          <h3>{{ rv5('perspectiveCognitiveTitle') }}</h3>
          <b>{{ rv5('perspectiveTaskCount', { n: cognitiveTaskCount }) }}</b>
          <small>{{ rv5('perspectiveCognitiveHint') }}</small>
          <span class="rv-expand-hint">{{ rv5('exploreResultsHint') }}</span>
        </summary>
        <div class="rv-domain-body">
          <p class="rv-footnote">{{ rv('cognitiveCaption') }}</p>
          <div class="cog-domain" *ngFor="let row of cognitiveRows">
            <div class="cog-row">
              <div class="cog-label">{{ row.label }}<span class="cog-subtitle" *ngIf="row.subtitle">{{ row.subtitle }}</span></div>
              <ng-container *ngIf="!row.notMeasured; else cogNotMeasured">
                <div class="cog-bar-bg">
                  <div class="cog-bar-fill" [style.width.%]="row.score"></div>
                </div>
                <div class="cog-band">{{ row.bandLabel }}</div>
              </ng-container>
              <ng-template #cogNotMeasured>
                <div class="cog-notmeasured">{{ 'doctorReport.reportView.v4.notMeasured' | translate }}</div>
              </ng-template>
            </div>
            <div class="cog-what" *ngIf="row.what">{{ row.what }}</div>
          </div>
          <!-- The bars place her; the table is every number behind them, one
               column per mapping, as the reference record prints it. -->
          <div class="cog-values" *ngIf="cognitiveValueTable as table">
            <p class="rv-footnote">{{ rv5('cogTableNote') }}</p>
            <table class="rv-table cog-value-table">
              <thead>
                <tr>
                  <th>{{ rv5('cogTableTask') }}</th>
                  <th *ngFor="let date of table.dates">{{ date }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of table.rows">
                  <th>{{ row.label }}</th>
                  <td *ngFor="let value of row.values">{{ value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="cog-context" *ngIf="cognitiveContextLine">{{ cognitiveContextLine }}</div>
          <div class="cog-disclaimer">{{ 'doctorReport.reportView.v4.cognitiveDisclaimer' | translate }}</div>
        </div>
      </details>

      <!-- 02 + 03 the two self-checks, each on its own card -->
      <details class="rv-domain" *ngIf="senseBlock as sense">
        <summary>
          <span class="rv-domain-no">{{ rv5('perspectiveVisionEyebrow') }}</span>
          <h3>{{ rv5('perspectiveVisionTitle') }}</h3>
          <b dir="ltr">{{ senseLatest('vision') }}</b>
          <small>{{ rv5('perspectiveLatest', { date: latestMappingDate }) }}</small>
          <span class="rv-expand-hint">{{ rv5('exploreResultsHint') }}</span>
        </summary>
        <div class="rv-domain-body">
          <ng-container *ngTemplateOutlet="senseTable; context: { $implicit: sense, rows: senseRowsFor('vision') }"></ng-container>
        </div>
      </details>

      <details class="rv-domain" *ngIf="senseBlock as sense">
        <summary>
          <span class="rv-domain-no">{{ rv5('perspectiveHearingEyebrow') }}</span>
          <h3>{{ rv5('perspectiveHearingTitle') }}</h3>
          <b dir="ltr">{{ senseLatest('hearing') }}</b>
          <small>{{ rv5('perspectiveLatest', { date: latestMappingDate }) }}</small>
          <span class="rv-expand-hint">{{ rv5('exploreResultsHint') }}</span>
        </summary>
        <div class="rv-domain-body">
          <ng-container *ngTemplateOutlet="senseTable; context: { $implicit: sense, rows: senseRowsFor('hearing') }"></ng-container>
        </div>
      </details>

      <!-- 04 what the selfie mapping recorded -->
      <details class="rv-domain" *ngIf="physiologicalSignalRows.length">
        <summary>
          <span class="rv-domain-no">{{ rv5('perspectiveSelfieEyebrow') }}</span>
          <h3>{{ rv5('perspectiveSelfieTitle') }}</h3>
          <b>{{ rv5('perspectiveFeatureCount', { n: physiologicalSignalRows.length }) }}</b>
          <small>{{ rv5('perspectiveSelfieHint') }}</small>
          <span class="rv-expand-hint">{{ rv5('exploreResultsHint') }}</span>
        </summary>
        <div class="rv-domain-body">
          <div class="domain-row" *ngFor="let row of physiologicalSignalRows">
            <div class="domain-label">{{ row.label }}</div>
            <div class="domain-score">{{ row.value }}</div>
            <div class="rv-signal-delta" *ngIf="row.delta">{{ row.delta }}</div>
          </div>
        </div>
      </details>
    </div>

    <!-- The self-check framing belongs to the section, said once. -->
    <p class="rv-footnote" *ngIf="senseBlock as sense">{{ tv('doctorReport.sense.framing') }}<ng-container *ngIf="sense.summary"> {{ sense.summary }}</ng-container><ng-container *ngIf="sense.alsoReported"> {{ sense.alsoReported }}</ng-container></p>
  </section>

  <!-- The sense rows of one metric, as the card reads them. -->
  <ng-template #senseTable let-sense let-rows="rows">
    <table class="sense-table" *ngIf="!sense.firstScan">
      <thead>
        <tr>
          <th class="s-metric"></th>
          <th class="s-change">{{ (sense.sinceDate ? 'doctorReport.sense.colChangeSince' : 'doctorReport.sense.colChange') | translate: { date: sense.sinceDate } }}</th>
          <th class="s-val">{{ 'doctorReport.sense.colValue' | translate }}</th>
          <th class="s-prev">{{ 'doctorReport.sense.colPrev' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="sense-row" *ngFor="let r of rows">
          <td class="s-metric">{{ r.labelKey | translate }}</td>
          <td class="s-change">{{ r.changeText }}</td>
          <td class="s-val" dir="ltr">{{ r.currentValue }}</td>
          <td class="s-prev" dir="ltr">{{ r.prevValue }}</td>
        </tr>
      </tbody>
    </table>

    <table class="sense-table" *ngIf="sense.firstScan">
      <thead>
        <tr>
          <th class="s-metric"></th>
          <th class="s-val">{{ 'doctorReport.sense.colValue' | translate }}</th>
          <th class="s-note">{{ 'doctorReport.sense.colNote' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="sense-row" *ngFor="let r of rows">
          <td class="s-metric">{{ r.labelKey | translate }}</td>
          <td class="s-val" dir="ltr">{{ r.currentValue }}</td>
          <td class="s-note">{{ r.note }}</td>
        </tr>
      </tbody>
    </table>
  </ng-template>

  <!-- HER SYMPTOM RATINGS, one card per group: how many she rated, the average,
       the highest, and the list itself one click away. -->
  <section class="rv-card">
    <div class="rv-panel-heading">
      <div>
        <h2>{{ rv5('exploreRatings') }}</h2>
        <p class="rv-footnote">{{ rv5('ratingsMeta', { n: allRatedSymptoms.length }) }}</p>
      </div>
      <input class="rv-search screen-only" type="search" [value]="symptomSearch" (input)="symptomSearch = $any($event.target).value" [attr.placeholder]="rv5('searchPlaceholder')" [attr.aria-label]="rv5('searchPlaceholder')">
    </div>

    <div class="rv-sgroup-grid">
      <details class="rv-sgroup" *ngFor="let group of explorerGroupCards">
        <summary>
          <div class="rv-sgroup-head">
            <h3>{{ group.label }}</h3>
            <span>{{ rv5(group.lines.length === 1 ? 'ratedSymptomsOne' : 'ratedSymptoms', { n: group.lines.length }) }}</span>
          </div>
          <div class="rv-sgroup-avg"><strong>{{ group.avgText }}</strong><span>/10</span></div>
          <div class="rv-track"><i [style.width.%]="group.avgPercent"></i></div>
          <small>{{ rv5('averageRating') }}</small>
          <p *ngIf="group.topLabel">{{ rv5('highestRated') }} {{ group.topLabel }} <b>{{ group.topScore }}/10</b></p>
          <span class="rv-expand-hint">{{ rv5('exploreRatingsHint') }}</span>
        </summary>
        <div class="rv-sgroup-body">
          <div class="cl-li" *ngFor="let line of group.lines">
            <span class="cl-li-nm">{{ line.label }}</span>
            <span class="cl-li-sc">{{ line.score }}/10</span>
            <span class="cl-li-dl" *ngIf="line.change">{{ line.change }}</span>
          </div>
        </div>
      </details>
    </div>
    <p class="rv-none" *ngIf="!explorerGroupCards.length">{{ rv5('searchEmpty') }}</p>

    <!-- The domain numbers she saw in her own results, kept beside her ratings. -->
    <details class="rv-quiet" *ngIf="domainScores.length">
      <summary><span>{{ 'doctorReport.reportView.v4.domainScoresTitle' | translate }}</span></summary>
      <p class="rv-footnote">{{ rv('domainScoresCaption') }}</p>
      <div class="domain-row" *ngFor="let row of domainScores">
        <div class="domain-label">{{ row.label }}</div>
        <div class="domain-track"><div class="domain-fill" [style.width.%]="valuePercent(row.value, 10)" [style.background]="row.color"></div></div>
        <div class="domain-score">{{ row.value }}</div>
      </div>
    </details>

    <!-- APPENDIX - every rated symptom, nothing filtered out. -->
    <details class="rv-quiet" *ngIf="allRatedSymptoms.length">
      <summary><span>{{ rv5('allRatings') }}</span><span>{{ rv5('ratingsCount', { n: allRatedSymptoms.length }) }}</span></summary>
      <div class="appendix-row" *ngFor="let row of allRatedSymptoms">
        <span class="appendix-sym">{{ row.label }}</span>
        <span class="appendix-score">{{ row.value }}/10</span>
        <span class="tag" [ngClass]="row.tierClass">{{ row.tier }}</span>
      </div>
    </details>
  </section>

  <!-- How to read the numbers above. -->
  <details class="rv-accordion rv-standalone-accordion">
    <summary>
      <span class="rv-acc-title"><b>{{ rv5('measurementContext') }}</b><small>{{ rv5('measurementContextHint') }}</small></span>
      <i aria-hidden="true">+</i>
    </summary>
    <div class="rv-acc-body">
      <p class="rv-footnote">{{ rv5('measurementContextBody') }}</p>
    </div>
  </details>

  <!-- Continuity note (fix 20): the two headline numbers the patient saw (symptom
       load + THINK), so the clinician recognizes them if she cites them. -->
  <div class="continuity-line" *ngIf="continuityLine">{{ continuityLine }}</div>

  <!-- Where to go next, the way the record offers it. -->
  <div class="rv-explorer-next screen-only">
    <span>{{ rv5('explorerNext') }}</span>
    <button class="rv-text-action" type="button" (click)="setTab('timeline')">{{ rv5('explorerNextAction') }} <svg class="rv-arrow" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg></button>
  </div>
</section>

<!-- FOOTER -->
<div class="footer">
  <p>{{ rv('footer1') }}</p>
  <p>{{ 'doctorReport.reportView.v4.footer2' | translate:{ date: (reportDate(data.checkDate) || '-') } }}</p>
</div>

<!-- REVIEW BEFORE COPYING TO RECORD -->
<dialog #ehrDialog class="rv-ehr-dialog screen-only" (close)="closeEhrDialog()">
  <div class="rv-ehr-head">
    <div>
      <h2>{{ rv5('ehrDialogTitle') }}</h2>
      <p>{{ rv5('ehrDialogSub') }}</p>
    </div>
    <button class="btn btn-outline" type="button" (click)="closeEhrDialog()">{{ rv5('ehrDialogClose') }}</button>
  </div>
  <textarea class="rv-ehr-box" [value]="ehrText" (input)="onEhrInput($any($event.target).value)"
            [attr.aria-label]="rv5('ehrDialogTitle')"></textarea>
  <div class="rv-ehr-foot">
    <button class="btn btn-primary" type="button" (click)="copyEhr()">{{ copyState || rv('copyToRecord') }}</button>
    @if (ehrEdited) {
      <button class="btn btn-outline" type="button" (click)="resetEhrDraft()">{{ rv5('ehrDialogReset') }}</button>
    }
  </div>
</dialog>

</div><!-- /page -->
